import React from "react";
import { ErrorNote } from "../components/ui.js";
import type { SiteFailure } from "./wizard.js";

type Choice = "ai" | "describe";

/**
 * Why the site could not be read, and the two ways on.
 *
 * The reason is the real one — blocked, slow, or not there — because "check
 * the address" on a site that merely blocks robots sends people hunting for a
 * typo that does not exist. A wrong address gets only that sentence: an AI
 * lookup of a name that is not a site would be a lookup of nothing.
 */
export const SiteFallback: React.FC<{
  failure: SiteFailure;
  choice: Choice;
  onChoice: (choice: Choice) => void;
  onContinue: () => void;
  busy?: boolean;
  error?: string | null;
}> = ({ failure, choice, onChoice, onContinue, busy, error }) => {
  if (!failure.offerAi) return <p className="text-[12px] text-signal-warn">{failure.message}</p>;

  const options: { key: Choice; title: string; body: string; suggested?: boolean }[] = [
    {
      key: "ai",
      title: "Let AI write a summary",
      body: "AI looks the company up on the public web and writes what it does. You read and edit it next, then choose the logo and colours.",
      suggested: true,
    },
    {
      key: "describe",
      title: "Describe it myself",
      body: "Write the name and a sentence or two. Logo and colours come from your brand kit, as today.",
    },
  ];

  return (
    <div className="flex flex-col gap-3 rounded-[10px] border border-[var(--edge)] bg-bay-900 p-4">
      <div>
        <p className="text-[12.5px] font-medium text-signal-warn">{failure.message}</p>
        <p className="mt-0.5 text-[12px] text-ink-400">Pick how to go on. Nothing has been charged.</p>
      </div>
      <div className="flex flex-col gap-2" role="radiogroup" aria-label="How to go on">
        {options.map((option) => (
          <button
            key={option.key}
            type="button"
            role="radio"
            aria-checked={choice === option.key}
            disabled={busy}
            onClick={() => onChoice(option.key)}
            className={`flex flex-col gap-0.5 rounded-[8px] border px-3.5 py-2.5 text-left transition-colors ${
              choice === option.key ? "border-ember-400 bg-bay-850" : "border-[var(--edge)] hover:border-ink-400"
            }`}
          >
            <span className="flex items-center gap-2 text-[12.5px] font-semibold text-ink-100">
              {option.title}
              {option.suggested ? (
                <span className="rounded-full bg-ember-400/10 px-1.5 py-px text-[10.5px] font-medium text-ember-600">
                  Suggested
                </span>
              ) : null}
            </span>
            <span className="text-[11.5px] leading-[1.5] text-ink-300">{option.body}</span>
          </button>
        ))}
      </div>
      {error ? <ErrorNote>{error}</ErrorNote> : null}
      <div>
        <button type="button" className="btn btn-primary" disabled={busy} onClick={onContinue}>
          Continue
        </button>
      </div>
    </div>
  );
};
